import { type FulfilledResult, type Result } from '../../utils';
import {
	type GetDirectAccessSettingFailureResponseValue,
	type GetDirectAccessSettingSuccessResponseValue,
	type ViralSettingsByDomain,
	type ViralSettingsByDomainValueObject,
	type ViralSettingsDESEligibleStates,
} from './types';

// Update the viral settings map based on domain results providing default values
export const createViralSettingsMapWithDefaultValues = (
	previousViralSettingMap: ViralSettingsByDomain,
	domainResults: ViralSettingsByDomainValueObject[],
): ViralSettingsByDomain => {
	const viralSettingsMap: ViralSettingsByDomain = {};
	domainResults.forEach((result) => {
		const domain = result.key;
		viralSettingsMap[domain] = {
			...result.value,
			isChecked: previousViralSettingMap[domain]
				? previousViralSettingMap[domain].isChecked
				: result.value.isChecked,
		};
	});
	return viralSettingsMap;
};

export const getDESEligibleDomainsStates = (
	viralSettingsMap: ViralSettingsByDomain,
): ViralSettingsDESEligibleStates => {
	const unchecked: string[] = [];
	const checked: string[] = [];

	const viralSettingsDomains = Object.keys(viralSettingsMap);

	viralSettingsDomains
		.filter((domain) => viralSettingsMap[domain].desPromotionEligible)
		.forEach((domain) => {
			const viralSetting = viralSettingsMap[domain];
			if (viralSetting.isChecked) {
				checked.push(domain);
			} else {
				unchecked.push(domain);
			}
		});
	return { checked, unchecked };
};

// To filter out just the ViralSettingsByDomainValueObject from the response array.
export const getViralSettingsByDomainValueObjectFromResponse = (
	domainResults: Result<ViralSettingsByDomainValueObject>[],
): ViralSettingsByDomainValueObject[] => {
	return domainResults
		.filter((result) => result.status === 'fulfilled')
		.map((item) => (item as FulfilledResult<ViralSettingsByDomainValueObject>).value);
};

// Get the domain from the successful result or rejected result
export const getDomainFromResults = (
	result: Result<
		GetDirectAccessSettingSuccessResponseValue,
		GetDirectAccessSettingFailureResponseValue
	>,
): string => {
	const successDomain =
		result.status === 'fulfilled' && result.value
			? (result.value as GetDirectAccessSettingSuccessResponseValue).domain
			: '';
	const errorDomain =
		result.status === 'failed' && result.errorDetails
			? (result.errorDetails as GetDirectAccessSettingFailureResponseValue).domain
			: '';
	// '' is falsy.
	return successDomain || errorDomain;
};

export const handlePromise = <T>(promise: Promise<T>) => {
	return promise
		.then((result) => Promise.resolve([result, undefined]))
		.catch((error) => Promise.resolve([undefined, error]));
};

export const createViralSettingsByDomainValueObject = (
	domain: string,
	isChecked: boolean,
	desPromotionEligible: boolean,
): ViralSettingsByDomainValueObject => {
	return {
		key: domain,
		value: {
			isChecked,
			desPromotionEligible,
		},
	};
};
